import { faCloudArrowDown, faEye, faFileCsv, faFilePdf, faTag } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useRef, useState } from 'react'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { TwinIconToolTip } from '../../../../../baseComponents/TwinIcon'
import twinFetchPost from '../../../../../utils/globals/data'
import { changeErrorMessage } from '../../../../../utils/reducers/reduxDispatch'
import LoadingSpinner from '../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { PdfTemplateResult } from '../../../../../utils/reducers/company/pdfTemplates'
import { PdfTemplatePdfTypesTy } from '@teinor/erp/types/company/config/pdfTemplate'
import useOnClickOutside from '../../../../../utils/hooks/useOnClickOut'
import OrderSelectorPdfTemplateConnect, { CsvOrderSelectorTemplateConnect } from '../../OrderLayout/OrderButtons'
import ModalPreviewPdfBuy, { ModalPreviewPdfBuyGet } from './ModalPreviewPDFBuy'
import usePdfTemplateTypes from '../../../../../utils/hooks/pdf/usePdfTemplateTypes'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import useCSVTemplateTypes from '../../../../../utils/hooks/csv/useCSVTemplateTypes'
import useTagTypes, { TagTypesTy } from '../../../../../utils/hooks/pdf/useTagTypes'
import ModalPrintTagsBuy from './ModalPrintTagsBuy'
import PermissionChecker from '../../../../../baseComponents/PermissionChecker'
import { getUserPermissions } from '../../../../../utils/reducers/getters'
import { ITIconToolTip } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'

interface BuyDownloadButtonPDFProps extends BuyDownloadButtonPDFLogicProps {
    id: string
    title: string
    className?: string
    children?: React.ReactNode
}
export const BuyDownloadButtonPDF: React.FC<BuyDownloadButtonPDFProps> = ({ id, title, className, children, ...logic }) => {
    const { downloadPdf, loading } = useBuyDownloadButtonPDFLogic({ ...logic })

    if (loading) {
        return <LoadingSpinner className='h-22' />
    }

    return (
        <div onClick={downloadPdf} >
            {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFilePdf} />}
            <a className='hidden downloadfile' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
        </div>
    )
}

interface BuyDownloadButtonPDFLogicProps {
    Order: TwinDictionary
    typeTemplate: keyof PdfTemplatePdfTypesTy
}

const useBuyDownloadButtonPDFLogic = ({ Order, typeTemplate }: BuyDownloadButtonPDFLogicProps) => {
    const { t } = useTwinTranslation()
    const errorNotPDFTemplate = t('notPDFTemplate', 'No tienes plantillas de PDF')
    const { endLoading, loading, startLoading } = useIsLoading()
    const { pdfTemplateTypes } = usePdfTemplateTypes()

    const downloadPdf = useCallback(async (e: React.MouseEvent) => {
        startLoading()
        const current = e.currentTarget
        const result = await twinFetchPost(pdfTemplateTypes?.[typeTemplate]?.url, { id: Order.id })
        if (result) {
            if (result.status === 422) {
                const val = await result.json()
                if (val?.errors[0] && val?.errors[0].msg === 'Not have PDF Template') {
                    changeErrorMessage(errorNotPDFTemplate)
                }
                endLoading()
                return false
            }
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Supplier?.fiscal_name}  ${Order?.Supplier?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [Order, errorNotPDFTemplate, typeTemplate, pdfTemplateTypes, endLoading, startLoading])

    return { downloadPdf, loading }
}

interface BuyDownloadPdfTemplateButtonProps {
    id: string
    Order: TwinDictionary
    title: string
    typeTemplate: keyof PdfTemplatePdfTypesTy
    className?: string
    children?: React.ReactNode
}

export const BuyDownloadPdfTemplateButton: React.FC<BuyDownloadPdfTemplateButtonProps> = ({ id, title, className, Order, typeTemplate, children }) => {
    const { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref } = useBuyDownloadPdfTemplateButtonLogic()
    return (
        <div className={'flex flex-col relative ' + (className || '')} ref={ref}>
            {loading
                ? <div><LoadingSpinner className='w-24 h-24 m-auto' /></div>
                :
                <div onClick={() => setShowTemplateSelector((old) => !old)}>
                    { children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faCloudArrowDown} /> }
                </div>
            }
            {showTemplateSelector ? <BuySelectorPdfTemplate Order={Order} handleShowTemplateSelector={() => setShowTemplateSelector((old) => !old)} startLoading={startLoading} endLoading={endLoading} typeTemplate={typeTemplate} /> : null}
        </div>
    )
}


const useBuyDownloadPdfTemplateButtonLogic = () => {
    const [showTemplateSelector, setShowTemplateSelector] = useState<boolean>(false)
    const { startLoading, endLoading, loading } = useIsLoading()
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => setShowTemplateSelector(false))

    return { ref, showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading }
}

interface BuySelectorPdfTemplateProps extends BuySelectorPdfTemplateLogicProps {}

const BuySelectorPdfTemplate: React.FC<BuySelectorPdfTemplateProps> = ({ ...rest }) => {
    const { ref, downloadPdfWithTemplate } = useBuySelectorPdfTemplateLogic({ ...rest })
    return (
        <OrderSelectorPdfTemplateConnect downloadPdfWithTemplate={downloadPdfWithTemplate} myref={ref} typeTemplate={rest.typeTemplate} />
    )
}

interface BuySelectorPdfTemplateLogicProps {
    Order: TwinDictionary
    handleShowTemplateSelector: () => void
    startLoading: () => true
    endLoading: () => void
    typeTemplate: keyof PdfTemplateResult
}

const useBuySelectorPdfTemplateLogic = ({ Order, handleShowTemplateSelector, startLoading, endLoading, typeTemplate }: BuySelectorPdfTemplateLogicProps) => {
    const ref = useRef<any>(null)

    const { pdfTemplateTypes } = usePdfTemplateTypes()
    const downloadPdfWithTemplate = useCallback(async (PdfTemplateId: number | string) => {
        startLoading()
        handleShowTemplateSelector()
        const current = ref.current
        const result = await twinFetchPost(pdfTemplateTypes[typeTemplate].url, { id: Order.id, PdfTemplateId })
        if (result) {
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [handleShowTemplateSelector, Order, endLoading, startLoading, pdfTemplateTypes, typeTemplate])

    return { ref, downloadPdfWithTemplate }
}

interface BuyPdfPreviewProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    children?: React.ReactNode
    typeTemplate: keyof PdfTemplateResult
}

export const BuyPdfPreview: React.FC<BuyPdfPreviewProps> = ({ id, Order, title, className, typeTemplate, children }) => {
    const { showPdfSelector, setShowPdfSelector, openModal, setOpenModal, handlePdfTemplate, ref } = useBuyPdfPreviewLogic()
    const { pdfTemplateTypes } = usePdfTemplateTypes()

    return (
        <div className={'flex flex-col relative ' + (className || '')}>
            <div onClick={() => setShowPdfSelector((old) => !old)}>
                { children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faEye} />}
            </div>
            {showPdfSelector ? <OrderSelectorPdfTemplateConnect typeTemplate={typeTemplate} downloadPdfWithTemplate={handlePdfTemplate} myref={ref} /> : null}
            {openModal?.type === 'edit' ? <ModalPreviewPdfBuy setOpened={() => setOpenModal(null)} onSubmit={() => { }} Order={Order} PdfTemplateId={openModal?.allRowData?.PdfTemplateId} urlGetPdf={pdfTemplateTypes[typeTemplate].url} /> : null}
        </div>
    )
}

const useBuyPdfPreviewLogic = () => {
    const ref = useRef<any>(null)
    const [showPdfSelector, setShowPdfSelector] = useState<boolean>(false)
    const { openModal, setOpenModal } = useEditDeleteModal()

    const handlePdfTemplate = useCallback((value: any) => {
        if (value) {
            setShowPdfSelector(false)
            setOpenModal({ type: 'edit', allRowData: { PdfTemplateId: value } })
        }
    }, [setOpenModal, setShowPdfSelector])

    return { showPdfSelector, setShowPdfSelector, openModal, setOpenModal, handlePdfTemplate, ref }
}


interface BuySupplierPdfPreviewProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    children?: React.ReactNode
}

export const BuySupplierPdfPreview: React.FC<BuySupplierPdfPreviewProps> = ({ id, Order, title, className, children }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return (
        <div className={'flex flex-col relative ' + (className || '')}>
            <div onClick={() => setOpenModal({ type: 'edit', allRowData: {} })}>
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faEye} />}
            </div>
            {openModal?.type === 'edit' ? <ModalPreviewPdfBuyGet setOpened={() => setOpenModal(null)} onSubmit={() => { }} urlGetPdf={Order.supplierPdf} /> : null}
        </div>
    )
}

interface BuyDownloadButtonCSVProps {
    id: string
    Order: TwinDictionary
    title: string
    typeTemplate: keyof PdfTemplateResult
    className?: string
    children?: React.ReactNode
}

export const BuyDownloadButtonCSV: React.FC<BuyDownloadButtonCSVProps> = ({ id, title, className, Order, typeTemplate, children }) => {
    const { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref } = useBuyDownloadCsvTemplateButtonLogic()
    return (
        <div className={'flex flex-col relative ' + (className || '')} ref={ref}>
            {loading
                ? <div><LoadingSpinner className='w-24 h-24 m-auto' /></div>
                :
                <div onClick={() => setShowTemplateSelector((old) => !old)}>
                    {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFileCsv} />}
                </div>
            }
            {showTemplateSelector ? <BuySelectorCsvTemplate Order={Order} handleShowTemplateSelector={() => setShowTemplateSelector((old) => !old)} startLoading={startLoading} endLoading={endLoading} typeTemplate={typeTemplate} /> : null}
        </div>
    )
}

const useBuyDownloadCsvTemplateButtonLogic = () => {
    const ref = useRef<any>(null)
    const [showTemplateSelector, setShowTemplateSelector] = useState<boolean>(false)
    const { startLoading, endLoading, loading } = useIsLoading()
    useOnClickOutside(ref, () => setShowTemplateSelector(false))

    return { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref }
}

interface BuySelectorCsvTemplateProps extends BuySelectorCsvTemplateLogicProps { }

const BuySelectorCsvTemplate: React.FC<BuySelectorCsvTemplateProps> = ({ ...rest }) => {
    const { ref, downloadCsvWithTemplate } = useBuySelectorCsvTemplateLogic({ ...rest })
    return (
        <CsvOrderSelectorTemplateConnect downloadCsvWithTemplate={downloadCsvWithTemplate} myref={ref} />
    )
}
interface BuySelectorCsvTemplateLogicProps {
    Order: TwinDictionary
    handleShowTemplateSelector: () => void
    startLoading: () => true
    endLoading: () => void
    typeTemplate: keyof PdfTemplateResult
}

const useBuySelectorCsvTemplateLogic = ({ Order, handleShowTemplateSelector, startLoading, endLoading, typeTemplate }: BuySelectorCsvTemplateLogicProps) => {
    const ref = useRef<any>(null)
    const { csvTemplateTypes } = useCSVTemplateTypes()
    const downloadCsvWithTemplate = useCallback(async (CsvOrderTemplateId: number | string) => {
        startLoading()
        handleShowTemplateSelector()
        const current = ref.current
        const result = await twinFetchPost(csvTemplateTypes[typeTemplate].url, { id: Order.id, CsvOrderTemplateId })
        if (result) {
            const csv = await result.blob()
            const fileURL = URL.createObjectURL(csv)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.csv'
                a.click()
            }
            endLoading()
        }
    }, [handleShowTemplateSelector, Order, endLoading, startLoading, typeTemplate, csvTemplateTypes])

    return { ref, downloadCsvWithTemplate }
}
interface BuyDownloadTagsProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    typeTemplate: TagTypesTy
    children?: React.ReactNode
}

export const BuyDownloadTags: React.FC<BuyDownloadTagsProps> = ({ id, Order, title, className, typeTemplate, children }) => {
    const { openModal, setOpenModal } = useBuyDownloadTagsLogic()
    const { tagTypes } = useTagTypes()

    return (
        <PermissionChecker userPermissions={getUserPermissions('config.label')} permission={'read'} >
            <div className={'flex flex-col relative ' + (className || '')}>
                <div onClick={() => setOpenModal({ type: 'edit', allRowData: {} })} >
                    {children ?? <ITIconToolTip id={id} classNameIcon={'regular24 hover:text-green-43 ' + (className || '')} message={title} icon={faTag} />}
                </div>
                {openModal?.type === 'edit' ? <ModalPrintTagsBuy setOpened={() => setOpenModal(null)} onSubmit={() => { }} Order={Order} urlGetTag={tagTypes[typeTemplate].url} /> : null}
            </div>
        </PermissionChecker>
    )
}

const useBuyDownloadTagsLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return { openModal, setOpenModal}
}